/**
 * The browser half of `MapProvider` — `docs/property-maps-v2.md` §9.1.
 *
 * The server mints the imagery session and hands back one tile-URL template
 * per base; this asks for it once per page and remembers it. Nothing here
 * knows the provider's name: `attribution` is a string the server sends, drawn
 * bottom-right, and a capture date is printed **only when the provider gives
 * one** (§12 rule 10) rather than guessed at.
 *
 * **One in-flight request, not one per map.** The editor, the print page and
 * the phone card could each open while the first answer is still coming back,
 * and three sessions where one would do is three calls to the provider and
 * three chances for one of them to be the one that fails. The promise itself
 * is the cache.
 */
import { request } from './api.js'

export type MapBaseKind = 'satellite' | 'map' | 'hybrid'

export interface ImagerySession {
  tiles: Record<MapBaseKind, string[]>
  maxZoom: number
  attribution: string
  /** Absent from Google's 2D Tiles API. Never invented (§9.1). */
  capturedAt?: string
  ttlSeconds: number
}

export interface GeocodeHit { label: string; lng: number; lat: number }

let pending: Promise<ImagerySession> | null = null
let got: { at: number; session: ImagerySession } | null = null

/** Re-ask next time — after a failure, and between tests. */
export function forgetImagery(): void {
  pending = null
  got = null
}

/**
 * The tiles, asked for at most once a session.
 *
 * `ttlSeconds` is the server's window, honoured here so a key changed in
 * Rowted Admin reaches a tab somebody left open rather than only a reload.
 */
export function imagerySession(): Promise<ImagerySession> {
  if (got && Date.now() - got.at < got.session.ttlSeconds * 1_000) {
    return Promise.resolve(got.session)
  }
  pending ??= request<ImagerySession>('/property-maps/imagery/session', { method: 'POST' })
    .then((session) => {
      got = { at: Date.now(), session }
      return session
    })
    .catch((cause: unknown) => {
      /* A failure must not be remembered, or **Try again** (§5.7) would be a
         button that re-reads the same rejection for ever. */
      pending = null
      throw cause
    })
  return pending
}

/** The *No map location* panel's address search (§5.1 state 1). */
export async function geocode(query: string): Promise<GeocodeHit[]> {
  const body = await request<{ candidates: GeocodeHit[] }>(
    `/property-maps/geocode?q=${encodeURIComponent(query)}`)
  return body.candidates
}
